import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AssetData,
  AssetsRequest,
  CreateRequest,
  UpdateRequest,
  PaginatedData,
  PostCommentRequest,
  ResponseData,
  ReviewData,
  ReviewsRequest,
  Label,
  PutReviewLabelsRequest,
  ReviewLabels, Network,
  ReviewExportData,
  ReviewExportDownloadData
} from '@models/online-reputation.models';
import { withFormattedPeriodRanges } from '@core/utils/period-range.util';

@Injectable({
  providedIn: 'root'
})
export class ReviewsService {
  private readonly httpClient = inject(HttpClient);

  listReviews(body: ReviewsRequest): Observable<PaginatedData<ReviewData>> {
    const url = body.text?.trim() ? 'api/reviews/search' : 'api/reviews';
    return this.httpClient.post<PaginatedData<ReviewData>>(url, withFormattedPeriodRanges(body));
  }

  listAssets(body: AssetsRequest): Observable<PaginatedData<AssetData>> {
    return this.httpClient.post<PaginatedData<AssetData>>('api/assets', body);
  }

  listLabels(): Observable<Label[]> {
    return this.httpClient.get<Label[]>('api/reviews/labels');
  }
  createAsset(body: CreateRequest): Observable<AssetData> {
    return this.httpClient.put<AssetData>('api/assets', body);
  }

  updateAsset(id: string, body: UpdateRequest): Observable<AssetData> {
    return this.httpClient.patch<AssetData>(`api/assets/${id}`, body);
  }

  deleteAsset(id: string): Observable<ResponseData<string>> {
    return this.httpClient.delete<ResponseData<string>>(`api/assets/${id}`);
  }

  postComment(body: PostCommentRequest): Observable<ResponseData<string>> {
    return this.httpClient.post<ResponseData<string>>('api/reviews/comment', body);
  }

  generateComment(reviewId: string): Observable<ResponseData<string>> {
    return this.httpClient.post<ResponseData<string>>(`api/reviews/${reviewId}/generateComment`, {});
  }

  tagReviewWithLabel(body: PutReviewLabelsRequest): Observable<number[]> {
    return this.httpClient.put<number[]>(`api/reviews/labels`, body);
  }

  /**
   * Starts a background Excel export of every review matching the filter and sort — all pages, so
   * the page token and search text aren't sent. `limit` is sent anyway (the export ignores it): the
   * web layer rejects a reviews request without it.
   */
  startExport(body: ReviewsRequest): Observable<ReviewExportData> {
    const { network, period, sort, limit } = withFormattedPeriodRanges(body);
    return this.httpClient.post<ReviewExportData>('api/reviews/exports', { network, period, sort, limit });
  }

  /** The user's exports that can be downloaded right now, newest first, each with its filter. */
  listExports(): Observable<ReviewExportData[]> {
    return this.httpClient.get<ReviewExportData[]>('api/reviews/exports');
  }

  getExport(id: string): Observable<ReviewExportData> {
    return this.httpClient.get<ReviewExportData>(`api/reviews/exports/${encodeURIComponent(id)}`);
  }

  /** A short-lived link that downloads the finished file straight from storage. */
  getExportDownload(id: string): Observable<ReviewExportDownloadData> {
    return this.httpClient.get<ReviewExportDownloadData>(`api/reviews/exports/${encodeURIComponent(id)}/download`);
  }
}
